/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { createAction } from '../../utils/redux-utils';
import {
    CashRegisterModelDetailsDto,
    VersionsListDto,
    CheckSumsListDto,
    StartAttestationDto,
    CompleteAttestationDto,
    ModelDocumentsListDto,
    FileInfo,
    SubmitDocumentsDto,
    UpdateModelDetailsParams,
    FactoryNumbersListDto,
    FactoryNumbersListFilterParams,
    VersionCheckSumsParams,
} from '../api/cashRegisterModels/cashRegisterModelsClient.typings';
import { Notification } from '../../utils/utils';
import { PageQueryParams, SortQueryParamsGeneric } from '../api/apiCommon.typings';
import { DocumentsListTableData } from '../../components/RegistryWidgets/CashRegisterModels/CashRegisterModelCard/CashRegisterModelTabs/DocumentsList/DocumentsListConfig';

export enum CashRegisterModelDetailsActionType {
    OPEN_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE = 'CashRegisterModelCard/ open page',
    EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE = 'CashRegisterModelCard/ exit page',

    FETCH_CASH_REGISTER_MODEL_DETAILS_SUCCESS = 'CashRegisterModelCard/ fetch details success',

    UPDATE_CASH_REGISTER_MODEL_DETAILS = 'CashRegisterModelCard/ update details of cash register model',
    UPDATE_CASH_REGISTER_MODEL_DETAILS_SUCCESS = 'CashRegisterModelCard/ update details of cash register model success',

    START_ATTESTATION = 'CashRegisterModelCard/ start attestation',
    START_ATTESTATION_SUCCESS = 'CashRegisterModelCard/ start attestation success',

    COMPLETE_ATTESTATION = 'CashRegisterModelCard/ complete attestation',
    COMPLETE_ATTESTATION_SUCCESS = 'CashRegisterModelCard/ complete attestation success',

    ACTIVATE_MODEL = 'CashRegisterModelCard/ activate cash register model',
    ACTIVATE_MODEL_SUCCESS = 'CashRegisterModelCard/ activate cash register model success',
    ELIMINATE_MODEL = 'CashRegisterModelCard/ eliminate cash register model',
    ELIMINATE_MODEL_SUCCESS = 'CashRegisterModelCard/ eliminate cash register model success',

    ACTIVATE_VERSION = 'CashRegisterModelCard/ activate software version',
    ACTIVATE_VERSION_SUCCESS = 'CashRegisterModelCard/ activate software version success',
    MARK_VERSION_OUTDATED = 'CashRegisterModelCard/ mark software version as outdated',
    MARK_VERSION_OUTDATED_SUCCESS = 'CashRegisterModelCard/ mark software version as outdated success',

    APPLY_DOCUMENTS_SORTING = 'CashRegisterModelCard/ apply documents sorting',
    APPLY_DOCUMENTS_PAGING = 'CashRegisterModelCard/ apply documents paging',

    FETCH_MODEL_DOCUMENTS_START = 'CashRegisterModelCard/ fetch model documents list start',
    FETCH_MODEL_DOCUMENTS_SUCCESS = 'CashRegisterModelCard/ fetch model documents list success',
    SUBMIT_DOCUMENTS_START = 'CashRegisterModelCard/ submit documents start',
    SUBMIT_DOCUMENTS_SUCCESS = 'CashRegisterModelCard/ submit documents success',
    DOWNLOAD_DOCUMENT_START = 'CashRegisterModelCard/ download document start',
    DOWNLOAD_DOCUMENT_SUCCESS = 'CashRegisterModelCard/ download document success',
    DELETE_DOCUMENTS_START = 'CashRegisterModelCard/ delete documents start',
    DELETE_DOCUMENTS_SUCCESS = 'CashRegisterModelCard/ delete documents success',
    DELETE_DOCUMENTS_ERROR = 'CashRegisterModelCard/ delete documents error',

    FETCH_SOFTWARE_VERSIONS_PAGE_START = 'CashRegisterModelCard/ fetch software versions page start',
    FETCH_SOFTWARE_VERSIONS_FULL_LIST_START = 'CashRegisterModelCard/ fetch software versions full list start',
    FETCH_SOFTWARE_VERSIONS_SUCCESS = 'CashRegisterModelCard/ fetch software versions success',
    APPLY_VERSIONS_PAGING = 'CashRegisterModelCard/ apply versions paging',
    FETCH_VERSION_CHECK_SUMS_START = 'CashRegisterModelCard/ fetch version check sums start',
    FETCH_VERSION_CHECK_SUMS_SUCCESS = 'CashRegisterModelCard/ fetch version check sums success',
    CHANGE_VERSION_VIEW = 'CashRegisterModelCard/ extend or collapse version view',
    COLLAPSE_ALL_VERSIONS_VIEWS = 'CashRegisterModelCard/ collapse all version views',

    FETCH_FACTORY_NUM_START = 'CashRegisterModelCard/ fetch factory numbers list start',
    FETCH_FACTORY_NUM_SUCCESS = 'CashRegisterModelCard/ fetch factory numbers list success',
    CHANGE_FACTORY_NUM_PAGING = 'CashRegisterModelCard/ change factory numbers list paging',
    CHANGE_FACTORY_NUM_FILTER = 'CashRegisterModelCard/ change factory numbers list filter',

    FETCH_CASH_REGISTER_MODEL_CARD_DATA_ERROR = 'CashRegisterModelCard/ fetch cash register card data error',

    CHANGE_INFO_TAB = 'CashRegisterModelCard/ change info tab',
}

export const openRegistryCashRegisterModelCardAction = (taxNumber: string) =>
    createAction(CashRegisterModelDetailsActionType.OPEN_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE, taxNumber);

export const exitRegistryCashRegisterModelCardAction = () =>
    createAction(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE);

export const fetchCashRegisterModelDetailsSuccessAction = (payload: CashRegisterModelDetailsDto) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_CASH_REGISTER_MODEL_DETAILS_SUCCESS, payload);

export const updateModelDetailsAction = (params: UpdateModelDetailsParams) =>
    createAction(CashRegisterModelDetailsActionType.UPDATE_CASH_REGISTER_MODEL_DETAILS, params);

export const updateModelDetailsSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.UPDATE_CASH_REGISTER_MODEL_DETAILS_SUCCESS);

export const startAttestationAction = (params: StartAttestationDto) =>
    createAction(CashRegisterModelDetailsActionType.START_ATTESTATION, params);

export const startAttestationSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.START_ATTESTATION_SUCCESS);

export const completeAttestationAction = (params: CompleteAttestationDto) =>
    createAction(CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION, params);

export const completeAttestationSuccessAction = (notification: Notification) =>
    createAction(CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION_SUCCESS, { notification });

export const activateModelAction = (comment: string) =>
    createAction(CashRegisterModelDetailsActionType.ACTIVATE_MODEL, comment);

export const activateModelSuccessAction = () => createAction(CashRegisterModelDetailsActionType.ACTIVATE_MODEL_SUCCESS);

export const eliminateModelAction = (comment: string) =>
    createAction(CashRegisterModelDetailsActionType.ELIMINATE_MODEL, comment);

export const eliminateModelSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.ELIMINATE_MODEL_SUCCESS);

export const activateVersionAction = (versionId: string) =>
    createAction(CashRegisterModelDetailsActionType.ACTIVATE_VERSION, versionId);

export const activateVersionSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.ACTIVATE_VERSION_SUCCESS);

export const markVersionOutdatedAction = (versionId: string) =>
    createAction(CashRegisterModelDetailsActionType.MARK_VERSION_OUTDATED, versionId);

export const markVersionOutdatedSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.MARK_VERSION_OUTDATED_SUCCESS);

export const applyDocumentsSortingAction = (params: SortQueryParamsGeneric<keyof DocumentsListTableData>) =>
    createAction(CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_SORTING, params);

export const applyDocumentsPagingAction = (params: PageQueryParams) =>
    createAction(CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_PAGING, params);

export const fetchModelDocumentsAction = () =>
    createAction(CashRegisterModelDetailsActionType.FETCH_MODEL_DOCUMENTS_START);

export const fetchModelDocumentsSuccessAction = (payload: ModelDocumentsListDto) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_MODEL_DOCUMENTS_SUCCESS, payload);

export const submitDocumentsAction = (params: SubmitDocumentsDto) =>
    createAction(CashRegisterModelDetailsActionType.SUBMIT_DOCUMENTS_START, params);

export const submitDocumentsSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.SUBMIT_DOCUMENTS_SUCCESS);

export const downloadDocumentAction = (file: FileInfo) =>
    createAction(CashRegisterModelDetailsActionType.DOWNLOAD_DOCUMENT_START, file);

export const downloadDocumentSuccessAction = () =>
    createAction(CashRegisterModelDetailsActionType.DOWNLOAD_DOCUMENT_SUCCESS);

export const deleteDocumentsAction = (params: string[]) =>
    createAction(CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_START, params);

export const deleteDocumentsSuccessAction = (notification: Notification) =>
    createAction(CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_SUCCESS, { notification });

export const deleteDocumentsErrorAction = (notification: Notification) =>
    createAction(CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_ERROR, { notification });

export const fetchSoftwareVersionsPageStartAction = () =>
    createAction(CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_PAGE_START);

export const fetchSoftwareVersionsFullListStartAction = () =>
    createAction(CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START);

export const fetchSoftwareVersionsSuccessAction = (payload: VersionsListDto) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_SUCCESS, payload);

export const applyVersionsPagingAction = (params: PageQueryParams) =>
    createAction(CashRegisterModelDetailsActionType.APPLY_VERSIONS_PAGING, params);

export const fetchVersionCheckSumsStartAction = (params: VersionCheckSumsParams) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_VERSION_CHECK_SUMS_START, params);

export const fetchVersionCheckSumsSuccessAction = (payload: {
    versionId: string;
    checkSumsHistory: CheckSumsListDto;
}) => createAction(CashRegisterModelDetailsActionType.FETCH_VERSION_CHECK_SUMS_SUCCESS, payload);

export const changeVersionViewAction = (viewStatus: { versionId: string; isExpanded: boolean }) =>
    createAction(CashRegisterModelDetailsActionType.CHANGE_VERSION_VIEW, viewStatus);

export const collapseAllVersionsViewsAction = () =>
    createAction(CashRegisterModelDetailsActionType.COLLAPSE_ALL_VERSIONS_VIEWS);

export const fetchFactoryNumbersListStartAction = () =>
    createAction(CashRegisterModelDetailsActionType.FETCH_FACTORY_NUM_START);

export const fetchFactoryNumbersListSuccessAction = (list: FactoryNumbersListDto) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_FACTORY_NUM_SUCCESS, list);

export const changeFactoryNumbersListPageParamsAction = (params: PageQueryParams) =>
    createAction(CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_PAGING, params);

export const changeFactoryNumbersListFilterParamsAction = (params: FactoryNumbersListFilterParams) =>
    createAction(CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_FILTER, params);

export const fetchCashRegisterModelCardDataErrorAction = (notification: Notification) =>
    createAction(CashRegisterModelDetailsActionType.FETCH_CASH_REGISTER_MODEL_CARD_DATA_ERROR, { notification });

export const changeInfoTabAction = (value: number) =>
    createAction(CashRegisterModelDetailsActionType.CHANGE_INFO_TAB, value);

export type CashRegisterModelDetailsAction =
    | ReturnType<typeof openRegistryCashRegisterModelCardAction>
    | ReturnType<typeof exitRegistryCashRegisterModelCardAction>
    | ReturnType<typeof fetchCashRegisterModelDetailsSuccessAction>
    | ReturnType<typeof updateModelDetailsAction>
    | ReturnType<typeof updateModelDetailsSuccessAction>
    | ReturnType<typeof startAttestationAction>
    | ReturnType<typeof startAttestationSuccessAction>
    | ReturnType<typeof completeAttestationAction>
    | ReturnType<typeof completeAttestationSuccessAction>
    | ReturnType<typeof activateModelAction>
    | ReturnType<typeof activateModelSuccessAction>
    | ReturnType<typeof eliminateModelAction>
    | ReturnType<typeof eliminateModelSuccessAction>
    | ReturnType<typeof activateVersionAction>
    | ReturnType<typeof activateVersionSuccessAction>
    | ReturnType<typeof markVersionOutdatedAction>
    | ReturnType<typeof markVersionOutdatedSuccessAction>
    | ReturnType<typeof fetchSoftwareVersionsPageStartAction>
    | ReturnType<typeof fetchSoftwareVersionsFullListStartAction>
    | ReturnType<typeof fetchSoftwareVersionsSuccessAction>
    | ReturnType<typeof applyVersionsPagingAction>
    | ReturnType<typeof fetchVersionCheckSumsStartAction>
    | ReturnType<typeof fetchVersionCheckSumsSuccessAction>
    | ReturnType<typeof changeVersionViewAction>
    | ReturnType<typeof collapseAllVersionsViewsAction>
    | ReturnType<typeof applyDocumentsSortingAction>
    | ReturnType<typeof applyDocumentsPagingAction>
    | ReturnType<typeof fetchModelDocumentsAction>
    | ReturnType<typeof fetchModelDocumentsSuccessAction>
    | ReturnType<typeof submitDocumentsAction>
    | ReturnType<typeof submitDocumentsSuccessAction>
    | ReturnType<typeof downloadDocumentAction>
    | ReturnType<typeof downloadDocumentSuccessAction>
    | ReturnType<typeof deleteDocumentsAction>
    | ReturnType<typeof deleteDocumentsSuccessAction>
    | ReturnType<typeof deleteDocumentsErrorAction>
    | ReturnType<typeof fetchFactoryNumbersListStartAction>
    | ReturnType<typeof fetchFactoryNumbersListSuccessAction>
    | ReturnType<typeof changeFactoryNumbersListPageParamsAction>
    | ReturnType<typeof changeFactoryNumbersListFilterParamsAction>
    | ReturnType<typeof fetchCashRegisterModelCardDataErrorAction>
    | ReturnType<typeof changeInfoTabAction>;
